import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  TrendingUp, 
  Sparkles, 
  AlertCircle, 
  Zap, 
  BarChart3, 
  ArrowRight,
  MousePointer2,
  DollarSign,
  RotateCcw,
  ShoppingCart,
  Percent,
  CheckCircle2,
  RefreshCw,
  Inbox
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip 
} from 'recharts';
import './Insights.css';
import { getApiUrl } from '../../config/api';


const API_URL = getApiUrl('/api/insights');
const getToken = () => localStorage.getItem('access_token') || '';


// Maps a neural_drivers label to a display icon since the API only returns label/val/color/progress
const DRIVER_ICON_MAP = {
  'fewer clicks': MousePointer2,
  'click': MousePointer2,
  'pricing concern': DollarSign,
  'pricing': DollarSign,
  'no repeat buyer': RotateCcw,
  'repeat': RotateCcw,
  'add to cart': ShoppingCart,
  'cart': ShoppingCart,
  'discount conversion': Percent,
  'discount': Percent,
  'purchase': CheckCircle2,
};

const getDriverIcon = (label = '') => {
  const key = label.toLowerCase();
  const match = Object.keys(DRIVER_ICON_MAP).find((k) => key.includes(k));
  return DRIVER_ICON_MAP[match] || Sparkles;
};


// Maps an insight's type to a display icon since the API only returns title/description/type
const INSIGHT_ICON_MAP = {
  positive: TrendingUp,
  negative: AlertCircle,
  neutral: Zap,
};

const getInsightIcon = (type = '') => INSIGHT_ICON_MAP[type] || Zap;


const Insights = () => {
  const [revenueTrendData, setRevenueTrendData] = useState([]);
  const [neuralDrivers, setNeuralDrivers] = useState([]);
  const [diagnosticInsights, setDiagnosticInsights] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [explainableAi, setExplainableAi] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();

      setDiagnosticInsights(Array.isArray(data?.insights) ? data.insights : []);
      setRevenueTrendData(Array.isArray(data?.revenue_trend_data) ? data.revenue_trend_data : []);
      setNeuralDrivers(Array.isArray(data?.neural_drivers) ? data.neural_drivers : []);
      setRecommendations(Array.isArray(data?.recommendations) ? data.recommendations : []);
      setExplainableAi(data?.explainable_ai || data?.explanation || '');
    } catch (err) {
      console.error('Insights fetch error:', err);
      setError('Unable to load AI insights data from the server.');
      setDiagnosticInsights([]);
      setRevenueTrendData([]);
      setNeuralDrivers([]);
      setRecommendations([]);
      setExplainableAi('');
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);


  const hasTrendData = revenueTrendData.length > 0;
  const hasDrivers = neuralDrivers.length > 0;
  const hasInsights = diagnosticInsights.length > 0;
  const hasRecommendations = recommendations.length > 0;


  return (
    <>
      <Helmet>
        <title>AI Insights - Revexia AI Revenue Brain</title>
        <meta name="description" content="Real-time AI insights and strategic recommendations" />
      </Helmet>


      <div className="insights-page-wrapper">
        <div className="container-fluid px-4">
          {/* Refresh / Error Bar */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            {error && (
              <div className="fallback-notice">
                <Inbox size={14} />
                <span>{error}</span>
              </div>
            )}
            <button
              className="attribution-refresh-btn ms-auto"
              onClick={fetchInsights}
              disabled={loading}
              aria-label="Refresh insights data"
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>


          {/* Top Section with Split Panel */}
          <div className="row g-4 mb-5">
            {/* Left: Neural Driver Metrics */}
            <div className="col-12 col-lg-4">
              <div className="neural-drivers-card">
                <div className="neural-drivers-header">
                  <div className="neural-header-content">
                    <div className="neural-icon-wrapper">
                      <Sparkles size={24} />
                    </div>
                    <h3 className="neural-title">Revenue Insights</h3>
                  </div>
                  <p className="neural-description">
                    Real-time weight volume assigned to cross-channel behavioral signals.
                  </p>
                </div>


                {loading ? (
                  <div className="attribution-state-card">
                    <div className="loading-spinner"></div>
                    <p>Loading drivers...</p>
                  </div>
                ) : !hasDrivers ? (
                  <div className="no-data-block">
                    <Inbox size={24} />
                    <p>No driver signals available yet</p>
                  </div>
                ) : (
                  <div className="neural-drivers-list">
                    {neuralDrivers.map((driver, i) => {
                      const Icon = getDriverIcon(driver.label);
                      return (
                        <div key={i} className="neural-driver-item">
                          <div className="neural-driver-header">
                            <div className="neural-driver-left">
                              <div className="neural-driver-icon">
                                <Icon size={14} />
                              </div>
                              <span className="neural-driver-label">{driver.label}</span>
                            </div>
                            <span className="neural-driver-value">{driver.val}</span>
                          </div>
                          <div className="neural-driver-progress-track">
                            <div 
                              className={`neural-driver-progress-bar progress-${driver.color}`}
                              style={{ width: `${driver.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}


                <div className="explainable-ai-card">
                  <p className="explainable-ai-label">Explainable AI Node</p>
                  <p className="explainable-ai-text">
                    "{explainableAi || (diagnosticInsights[0]?.description || diagnosticInsights[0]?.explanation) || 'Purchase signals and neural drivers are actively monitored across all customer touchpoints.'}"
                  </p>
                </div>
              </div>
            </div>


            {/* Right: Revenue Anomalies Chart */}
            <div className="col-12 col-lg-8">
              <div className="revenue-anomalies-section">
                <div className="anomalies-header">
                  <div className="anomalies-header-left">
                    <BarChart3 size={20} className="anomalies-icon" />
                    <h3 className="anomalies-title">Revenue Anomalies</h3>
                  </div>
                  <div className="anomalies-legend">
                    <div className="legend-item">
                      <span className="legend-dot blue"></span>
                      <span className="legend-text">Predicted</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot cyan"></span>
                      <span className="legend-text">Realized</span>
                    </div>
                  </div>
                </div>
                <div className="anomalies-chart-card">
                  <div className="chart-bg-glow"></div>
                  <div className="chart-container">
                    {loading ? (
                      <div className="attribution-state-card">
                        <div className="loading-spinner"></div>
                        <p>Loading revenue trend...</p>
                      </div>
                    ) : !hasTrendData ? (
                      <div className="no-data-block">
                        <Inbox size={26} />
                        <p>No revenue trend data available</p>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueTrendData}>
                          <defs>
                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22D3EE" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#22D3EE" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} 
                            dy={10}
                          />
                          <YAxis 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} 
                            tickFormatter={(val) => `$${val/1000}k`}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'rgba(13, 15, 41, 0.95)', 
                              border: '1px solid rgba(255,255,255,0.1)', 
                              borderRadius: '12px',
                              backdropFilter: 'blur(4px)'
                            }}
                            itemStyle={{ color: '#fff' }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#22D3EE" 
                            strokeWidth={3} 
                            fillOpacity={1} 
                            fill="url(#colorRevenue)" 
                            animationDuration={2000}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Diagnostic Insights Section */}
          <section className="diagnostic-insights-section">
            <div className="section-header-with-icon">
              <Sparkles size={20} className="section-icon purple" />
              <h3 className="section-main-title">Diagnostic Insights</h3>
            </div>
            {loading ? (
              <div className="attribution-state-card">
                <div className="loading-spinner"></div>
                <p>Loading insights...</p>
              </div>
            ) : !hasInsights ? (
              <div className="no-data-block">
                <Inbox size={26} />
                <p>No diagnostic insights available yet</p>
              </div>
            ) : (
              <div className="row g-4">
                {diagnosticInsights.map((insight, index) => {
                  const Icon = getInsightIcon(insight.type);
                  return (
                    <div key={index} className="col-12 col-md-4">
                      <div className={`insight-card insight-${insight.type}`}>
                        <div className={`insight-glow ${insight.type}`}></div>
                        <div className="insight-content">
                          <div className={`insight-icon-wrapper ${insight.type}`}>
                            <Icon size={20} />
                          </div>
                          <div className="insight-text">
                            <h4 className="insight-title">{insight.title}</h4>
                            <p className="insight-description">{insight.description}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>


          {/* Strategic Recommendations Section */}
          <section className="recommendations-section">
            <div className="section-header-with-icon">
              <AlertCircle size={20} className="section-icon orange" />
              <h3 className="section-main-title">Strategic Recommendations</h3>
            </div>
            {loading ? (
              <div className="attribution-state-card">
                <div className="loading-spinner"></div>
                <p>Loading recommendations...</p>
              </div>
            ) : !hasRecommendations ? (
              <div className="no-data-block">
                <Inbox size={26} />
                <p>No recommendations available yet</p>
              </div>
            ) : (
              <div className="recommendations-list">
                {recommendations.map((rec, index) => (
                  <div key={index} className="recommendation-card">
                    <div className="recommendation-left">
                      <div className="recommendation-icon-wrapper">
                        <Zap size={22} />
                      </div>
                      <div className="recommendation-info">
                        <h5 className="recommendation-title">{rec.title}</h5>
                        <div className="recommendation-meta">
                          <span className={`recommendation-impact impact-${(rec.impact || '').toLowerCase()}`}>
                            <TrendingUp size={12} /> {rec.impact} Impact
                          </span>
                          <span className="recommendation-divider">•</span>
                          <span className="recommendation-duration">Est. {rec.duration} to implement</span>
                        </div>
                      </div>
                    </div>
                    <button className="recommendation-action-btn">
                      <span>{rec.action}</span>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
};


export default Insights;